import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

function FlipReviewCard({ d, i }: { d: ResultDetail; i: number }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div className="perspective">
      <div
        className={`flip-card relative min-h-[160px] w-full cursor-pointer ${flipped ? "rotate-y-180" : ""}`}
        onClick={() => setFlipped((v) => !v)}
      >
        <Card className={`absolute inset-0 backface-hidden min-h-[160px] ${d.correct ? "border-green-600/40" : "border-red-600/40"}`}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start gap-2">
              {d.correct ? <Check className="h-5 w-5 text-green-600 mt-0.5" /> : <X className="h-5 w-5 text-red-600 mt-0.5" />}
              <div className="font-medium">{i + 1}. {d.question}</div>
            </div>
            <div className="text-sm">Your answer: <span className={d.correct ? "text-green-600" : "text-red-600"}>{d.user_answer || "-"}</span></div>
            <div className="text-xs text-muted-foreground pt-2">Tap to see correct answer</div>
          </CardContent>
        </Card>
        <Card className="absolute inset-0 backface-hidden rotate-y-180 min-h-[160px] border-primary/40">
          <CardContent className="p-4 h-full flex flex-col items-center justify-center text-center space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Correct answer</div>
            <div className="text-2xl font-semibold text-green-600">{d.correct_answer}</div>
            <div className="text-xs text-muted-foreground">Tap to flip back</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

type QuestionRow = {
  id: string;
  test_id: string;
  question: string;
  question_type: string;
  options: any;
  sort_order: number;
};

type ResultDetail = {
  id: string;
  question: string;
  options: any;
  correct_answer: string;
  explanation: string | null;
  user_answer: string | null;
  correct: boolean;
};

type SubmitResult = {
  score: number;
  total: number;
  passed: boolean;
  pass_score: number;
  details: ResultDetail[];
};

export default function TestRunner() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [test, setTest] = useState<any>(null);
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: t } = await supabase.from("tests").select("*").eq("slug", slug).maybeSingle();
      if (!t) return;
      setTest(t);
      const { data: q, error } = await supabase.rpc("get_test_questions", { p_test_id: t.id });
      if (error) {
        toast.error("Failed to load questions");
        return;
      }
      setQuestions((q || []) as QuestionRow[]);
    })();
  }, [slug]);

  async function submit() {
    if (!user || !test || submitting) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("submit_test_attempt", {
      p_test_id: test.id,
      p_answers: answers,
    });
    setSubmitting(false);
    if (error || !data) {
      toast.error(error?.message || "Failed to submit");
      return;
    }
    const res = data as unknown as SubmitResult;
    await supabase.rpc("bump_streak", { p_xp: res.passed ? 10 : 3 });
    setResult(res);
    toast.success(res.passed ? "Passed! 🎉" : "Keep practicing!");
  }

  if (!test) return <p>Loading…</p>;

  if (result) {
    const pct = Math.round((result.score / result.total) * 100);
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{result.passed ? "🎉 You passed!" : "Keep practicing"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-4xl font-bold">{result.score} / {result.total}</div>
            <div className="text-muted-foreground">{pct}% - pass mark {result.pass_score}%</div>
          </CardContent>
        </Card>
        <p className="text-sm text-muted-foreground text-center">Tap any card to flip and see the correct answer.</p>
        <div className="space-y-3">
          {result.details.map((d, i) => (
            <FlipReviewCard key={d.id} d={d} i={i} />
          ))}
        </div>
        <Button asChild><Link to="/tests">Back to tests</Link></Button>
      </div>
    );
  }

  const answered = Object.keys(answers).length;
  const total = questions.length;
  const allAnswered = total > 0 && answered === total;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{test.title}</h1>
        <p className="text-muted-foreground">{test.description}</p>
        <p className="text-sm mt-2">Answered <span className="font-semibold">{answered}</span> of {total}</p>
      </div>
      {total === 0 && (
        <Card><CardContent className="p-6 text-muted-foreground">No questions in this test yet.</CardContent></Card>
      )}
      {questions.map((q, i) => {
        const opts = Array.isArray(q.options) ? q.options : [];
        return (
          <Card key={q.id}>
            <CardHeader><CardTitle className="text-base">{i + 1}. {q.question}</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {opts.length > 0 ? (
                opts.map((opt: string) => (
                  <label key={opt} className={`flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-accent ${answers[q.id] === opt ? "border-primary bg-accent" : ""}`}>
                    <input
                      type="radio"
                      name={q.id}
                      checked={answers[q.id] === opt}
                      onChange={() => setAnswers({ ...answers, [q.id]: opt })}
                    />
                    <span>{opt}</span>
                  </label>
                ))
              ) : (
                <input
                  type="text"
                  placeholder="Type your answer"
                  className="w-full p-2 rounded border bg-background"
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                />
              )}
            </CardContent>
          </Card>
        );
      })}
      {total > 0 && !allAnswered && (
        <p className="text-sm text-center text-muted-foreground">Please answer all {total} questions to submit ({total - answered} left).</p>
      )}
      <Button onClick={submit} disabled={submitting || !allAnswered} size="lg" className="w-full">
        {submitting ? "Submitting…" : allAnswered ? "Submit answers" : `Answer all questions (${answered}/${total})`}
      </Button>
    </div>
  );
}
